import { IPassportSchema } from '@/pages/MDM/mdm.types.ts'
import SidePage from '@/shared/SidePage'
import useTreeSelectCtrl from '@/shared/Tree/ctrl/useTreeSelectCtrl.ts'
import PassportSchemaGroupsTree from '@/pages/MDM/components/Passports/PassportSchemaSidePage/PassportSchemaGroupsTree'
import useStateWithChange from '@/shared/hooks/useStateWithChange.ts'
import useNestedArrayChangeCallbacks from '@/shared/hooks/useNestedArrayChangeCallbacks.ts'
import mdmUtils from '@/pages/MDM/mdm.utils.ts'
import PassportFieldsForm from '@/pages/MDM/components/Passports/PassportSchemaSidePage/PassportFieldsForm'
import SeparateLine from '@/shared/Forms/SeparateLine'
import Button from '@/shared/Buttons/Button'
import useHttpLoader from '@/shared/hooks/useHttpLoader.ts'
import mdmApi from '@/pages/MDM/mdm.api.ts'
import mdmEvents from '@/pages/MDM/events/mdm.events.ts'

interface IProps {
  passport: IPassportSchema
  open: boolean
  onClose: () => void
  onCloseAnimationEnd?: () => void
}

const PassportSchemaSidePage = (props: IProps) => {
  const { wait, loading } = useHttpLoader()
  const treeSelectCtrl = useTreeSelectCtrl(`/mdm/edit-passport/${props.passport.id}`, null)

  const [passportSchema, setPassportSchema] = useStateWithChange(props.passport)

  const groupFields = passportSchema.fields
    .map((f, index) => ({ ...f, sourceIndex: index }))
    .filter((f) => f.groupId === treeSelectCtrl.selectedItem)

  const {
    handleCreateNewElement: handleValueCreate,
    handleElementUpdate: handleValueUpdate,
    handleElementDelete: handleValueDelete,
  } = useNestedArrayChangeCallbacks({
    sourceObjectSetter: setPassportSchema,
    nestedArrayKey: 'fields',
    newEntityGenerator: () =>
      mdmUtils.generatePassportField(groupFields.length, treeSelectCtrl.selectedItem, props.passport.id),
  })

  const handleSave = () => {
    wait(mdmApi.updatePassportSchema(passportSchema, props.passport.id), (resp) => {
      if (resp.status === 'success') {
        mdmEvents.passportSchemaUpdated(resp.body)
        props.onClose()
      }
    })
  }

  return (
    <SidePage
      onClose={props.onClose}
      open={props.open}
      size="large"
      style={{ paddingRight: 0 }}
      onCloseAnimationEnd={props.onCloseAnimationEnd}
    >
      <div className="flex-lines gap16 height100">
        <h4 className="text-heading-4">Редактирование схемы паспорта</h4>
        <SeparateLine />
        <div className="inline-flex-gap gap16 height100" style={{ minHeight: 0 }}>
          <div
            style={{
              width: '280px',
              borderRight: '1px solid var(--border-secondary-color)',
              height: '100%',
              paddingRight: '12px',
              minHeight: 0,
            }}
          >
            <PassportSchemaGroupsTree selectCtrl={treeSelectCtrl} passportSchema={props.passport} />
          </div>
          <div
            className="flex-lines gap16 width100"
            style={{ overflow: 'auto', paddingRight: '24px', height: '100%', minHeight: 0 }}
          >
            <PassportFieldsForm
              fields={groupFields}
              onFieldChange={handleValueUpdate}
              onFieldDelete={handleValueDelete}
              onFieldCreate={handleValueCreate}
            />
            <div style={{ marginTop: 'auto' }}>
              <SeparateLine />
            </div>
            <div className="inline-flex-gap gap8">
              <Button text="Сохранить" size="default" type="primary" loading={loading} onClick={handleSave} />
              <Button size="default" type="default" text="Отменить" onClick={props.onClose} disabled={loading} />
            </div>
          </div>
        </div>
      </div>
    </SidePage>
  )
}

export default (props: IProps) => {
  if (!props.passport) return null

  return <PassportSchemaSidePage {...props} />
}
